import { ref, reactive, watch, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { useRegisterStore } from '@/stores/register-store'
import { useLoginStore } from '@/stores/login-store'
import API from '@/utils/constants/apiUrls'
import useMessage from '@/utils/composables/useMessage'
import apiService from '@/utils/services/apiService'
import tokenService from '@/utils/services/tokenService'

export function useCreateAgainPinForm() {
  const router = useRouter()
  const route = useRoute()
  const registerStore = useRegisterStore()
  const loginStore = useLoginStore()
  const { showMessage, getErrorMessage } = useMessage()

  const ruleFormRef = ref()
  const loading = reactive({ form: false })
  const pinLength = 6
  const pin = ref<string[]>(Array(pinLength).fill(''))
  const form = reactive({ pin: '' })
  const inputRefs = ref<(HTMLInputElement | null)[]>([])
  const errorState = reactive({ pinNotMatch: false as boolean })

  const setInputRef = (el: HTMLInputElement | null, index: number) => {
    if (el) inputRefs.value[index] = el
  }

  onMounted(() => {
    // Update stores with query params data for registration flow if available
    if (route.query.flow === 'register' && route.query.phone) {
      registerStore.setRegisterData({
        phone: route.query.phone as string,
        countryCode: (route.query.countryCode as string) || '+62',
        fullname: (route.query.fullname as string) || '',
        email: (route.query.email as string) || ''
      })
    }

    if (registerStore.pin) {
      // Optionally, bisa prefill jika mau
    }
  })

  const onInput = (event: Event, index: number) => {
    const target = event.target as HTMLInputElement
    let val = target.value.replace(/[^0-9]/g, '')
    if (val.length > 1) val = val.slice(-1)
    pin.value[index] = val
    target.value = val

    if (errorState.pinNotMatch) {
      errorState.pinNotMatch = false
      inputRefs.value[0]?.focus()
    }
    if (val && index < pinLength - 1) {
      inputRefs.value[index + 1]?.focus()
    }
    form.pin = pin.value.join('')
  }

  const onKeydown = (event: KeyboardEvent, index: number) => {
    const target = event.target as HTMLInputElement
    if (event.key === 'Backspace' && !target.value && index > 0) {
      inputRefs.value[index - 1]?.focus()
    }
  }

  watch(pin, () => {
    form.pin = pin.value.join('')
  })

  const rules = {
    pin: [
      { required: true, message: 'PIN wajib diisi', trigger: 'blur' },
      { min: 6, max: 6, message: 'PIN harus 6 digit', trigger: 'blur' },
      {
        pattern: /^\d{6}$/,
        message: 'PIN harus berupa 6 digit angka',
        trigger: 'blur'
      }
    ]
  }

  const handleSubmit = async (formEl: any) => {
    if (!formEl) return
    form.pin = pin.value.join('')
    // Pastikan semua kolom terisi 1 digit
    if (pin.value.some((d) => d === '' || d.length !== 1)) {
      const firstEmpty = pin.value.findIndex((d) => d === '' || d.length !== 1)
      if (firstEmpty !== -1) inputRefs.value[firstEmpty]?.focus()
      return
    }

    // Determine if this is forgot PIN flow or registration flow
    const isForgotPinFlow =
      loginStore.isRegistered ||
      (loginStore.otpToken && loginStore.phone) ||
      (!registerStore.fullname && !registerStore.email && !route.query.flow)

    // Validasi pin konfirmasi harus sama dengan pin utama
    let mainPin = ''
    if (isForgotPinFlow) {
      mainPin = loginStore.pin
    } else {
      mainPin = registerStore.pin
    }

    if (form.pin !== mainPin) {
      errorState.pinNotMatch = true
      pin.value = Array(pinLength).fill('')
      form.pin = ''
      inputRefs.value[0]?.focus()
      // errorState.pinNotMatch tetap true agar warna error tetap tampil
      return
    } else {
      errorState.pinNotMatch = false
    }

    await formEl.validate(async (valid: boolean) => {
      if (!valid) return

      // Simpan confirmPin ke store yang sesuai
      if (isForgotPinFlow) {
        loginStore.setLoginData({ confirmPin: form.pin })
      } else {
        registerStore.setRegisterData({ confirmPin: form.pin })
      }

      try {
        loading.form = true
        let payload: any
        let apiUrl: string

        if (isForgotPinFlow) {
          // Forgot PIN flow
          let phone = loginStore.phone || ''
          const countryCode = (loginStore.countryCode || '').replace(/^\+/, '')

          // Clean phone number formatting
          phone = phone.replace(/^0+/, '') // Remove leading zeros

          // Add country code if not present
          if (
            !phone.startsWith('62') &&
            !phone.startsWith('1') &&
            !phone.startsWith('44')
          ) {
            phone = `${countryCode || '62'}${phone}`
          }

          payload = {
            pin: loginStore.pin,
            confirmPin: form.pin,
            otpToken: loginStore.otpToken,
            phone: phone
          }
          apiUrl = API.CMS.POST_FORGOT_SET_PIN
        } else {
          // Registration flow
          // Gabungkan kode negara dan phone untuk payload
          let phone = registerStore.phone || ''
          let countryCode = registerStore.countryCode || ''

          // Clean phone number formatting
          phone = phone.replace(/^0+/, '') // Remove leading zeros

          // Clean country code
          if (countryCode.startsWith('+')) {
            countryCode = countryCode.slice(1)
          }

          // Add country code if not present
          if (
            !phone.startsWith('62') &&
            !phone.startsWith('1') &&
            !phone.startsWith('44')
          ) {
            phone = `${countryCode || '62'}${phone}`
          }

          payload = {
            fullname: registerStore.fullname,
            pin: registerStore.pin,
            confirmPin: form.pin,
            otpToken: registerStore.otpToken,
            phone: phone,
            ...(registerStore.email && { email: registerStore.email })
          }
          apiUrl = API.CMS.POST_SET_PIN
        }

        const response = await apiService.post(apiUrl, payload)

        if (isForgotPinFlow) {
          router.push({ name: 'PinChangeSuccessPage' })
        } else {
          // Cek dua kemungkinan struktur response sukses
          const tokenData = response?.data?.data?.token
            ? response.data.data
            : response?.data
          if (tokenData?.token && tokenData?.tokenExpired) {
            tokenService.saveTokenToLocalStorage(
              tokenData.token,
              tokenData.tokenExpired
            )
            router.push({ name: 'RegisterSuccessPage' })
          }
        }
      } catch (error: any) {
        showMessage('error', getErrorMessage(error))
      } finally {
        loading.form = false
      }
    })
  }

  return {
    form,
    rules,
    ruleFormRef,
    loading,
    pinLength,
    pin,
    inputRefs,
    setInputRef,
    errorState,
    onInput,
    onKeydown,
    handleSubmit
  }
}
